/**
 * Enveloppe des écrans de régie (SPEC-BOUTIQUE §7).
 *
 * ★ LIB-04 — CE BANDEAU EST SUR TOUS LES ÉCRANS DE RÉGIE, donc il doit être
 * vrai sur tous les lieux. Il annonçait « échanger deux fauteuils, libérer un
 * siège » : sur un open-air ou une halle, il n'y a ni fauteuil ni siège, et la
 * régie y sert quand même. Le garde-fou de rendu
 * (`lib/ecrans-vocabulaire.test.tsx`) l'a relevé sur les six écrans à la fois —
 * personne n'avait pensé à lister cette phrase-là, c'est justement son objet.
 *
 * ★ Un rappel permanent en tête d’écran, et il n’est pas décoratif : ces
 * outils contournent des **règles de gestion** — la phase d’une saison, la
 * promesse faite à un abonné, l’heure d’expiration d’un panier. Ils ne
 * contournent jamais un invariant : deux personnes ne peuvent pas se retrouver
 * sur le même fauteuil, quoi qu’on demande ici. Quand le serveur refuse, il
 * dit pourquoi, et l’écran le répète mot pour mot.
 *
 * Réservé à l’administrateur du tenant — le guichet ne voit pas ces écrans
 * (garde de route `RequireStaff roles={['admin']}` dans `router.tsx`, et garde
 * de plugin `requireRole('admin')` côté serveur, qui est celle qui fait foi).
 */

import { NavLink, Outlet } from 'react-router'

import { cn } from '../../lib/cn'
import { estEvenement, type Lexique, useLexique, useTypeTenant } from '../../lib/vocabulaire'
import {
  CHEMIN_ABONNEMENTS,
  CHEMIN_DIAGNOSTIC,
  CHEMIN_ESPACE_ABONNE,
  CHEMIN_PANIERS_REGIE,
  CHEMIN_INTERVENTIONS,
  CHEMIN_PHASES,
} from './chemins'

interface Onglet {
  readonly to: string
  readonly libelle: string
  /**
   * ★ EVT-04 — `true` quand l'outil a l'ABONNEMENT pour sujet : la série qu'on
   * déplace, la carte qu'on transfère, l'espace qu'on consulte pour dépanner un
   * abonné. Un tenant `evenement` n'a ni formule ni souscription (EVT-02) — ces
   * onglets ne mènent nulle part, et leur adresse renvoie au diagnostic
   * (`features/deblocage/index.tsx`).
   */
  readonly masqueSiEvenement?: boolean
  /**
   * Sous-titre de l’onglet : ce que l’outil permet de FAIRE, en mots.
   * Surtout pas la référence d’exigence — l’administrateur d’un théâtre ne
   * lit pas notre spécification (garde-fou `textes-interface.test.ts`).
   */
  readonly resume: string
}

function onglets(mots: Lexique): readonly Onglet[] {
  return [
    /* ★ LIB-04 — « Places » et non « Sièges ». La régie sert aussi un lieu à
       placement libre, où il n'y a pas de siège à nommer ; « place » est le seul
       mot vrai des deux côtés, et il ne perd rien pour une salle numérotée. */
    {
      to: CHEMIN_DIAGNOSTIC,
      libelle: 'Places',
      resume: 'Diagnostiquer, déplacer, échanger, libérer',
    },
    {
      to: CHEMIN_ABONNEMENTS,
      libelle: 'Abonnements',
      resume: 'Déplacer la série, transférer',
      masqueSiEvenement: true,
    },
    { to: CHEMIN_PANIERS_REGIE, libelle: 'Paniers', resume: 'Forcer l’expiration' },
    { to: CHEMIN_INTERVENTIONS, libelle: 'Demandes', resume: 'Répondre au guichet' },
    {
      to: CHEMIN_PHASES,
      /* ★ « Phases de saison », sans article — c'est l'intitulé du produit, et
         `deSaison` porte l'élision (« d'édition ») que `de ${mots.saison}` ne
         saurait pas faire. */
      libelle: `Phases ${mots.deSaison}`,
      resume: 'Rouvrir une phase fermée',
    },
    {
      to: CHEMIN_ESPACE_ABONNE,
      libelle: 'Espace d’un abonné',
      resume: 'Consulter ce qu’il voit',
      masqueSiEvenement: true,
    },
  ]
}

export function LayoutRegie() {
  const mots = useLexique()
  const evenement = estEvenement(useTypeTenant())
  const ongletsVisibles = onglets(mots).filter(
    (onglet) => !(evenement && onglet.masqueSiEvenement === true),
  )

  return (
    <div className="flex flex-col gap-6 p-4 sm:p-6">
      <header className="flex flex-col gap-2">
        <h1 className="text-xl font-semibold text-slate-900">Régie</h1>
        {/* ★ EVT-04 — ce bandeau est sur TOUS les écrans de régie : il énumère
            les gestes possibles, et deux d'entre eux — déplacer un abonné,
            transférer un abonnement — n'existent pas chez un organisateur
            ponctuel. On ne remplace pas leurs mots, on retire les gestes :
            annoncer un outil absent est le défaut même que ce lot répare. */}
        <p className="max-w-3xl text-sm text-slate-600">
          {evenement
            ? 'Les outils de déblocage : comprendre pourquoi une place est prise, échanger deux places, libérer une place, forcer un panier, rouvrir une phase. Chaque geste exige un motif et laisse une trace nominative au journal.'
            : 'Les outils de déblocage : comprendre pourquoi une place est prise, déplacer un abonné, échanger deux places, libérer une place, forcer un panier, transférer un abonnement, rouvrir une phase. Chaque geste exige un motif et laisse une trace nominative au journal.'}
        </p>
      </header>

      <nav aria-label="Outils de régie" className="-mx-1 overflow-x-auto">
        <ul className="flex min-w-max gap-1 px-1">
          {ongletsVisibles.map((onglet) => (
            <li key={onglet.to}>
              <NavLink
                to={onglet.to}
                className={({ isActive }) =>
                  cn(
                    'flex min-h-11 flex-col justify-center rounded-md px-3 py-2 text-sm transition-colors',
                    isActive ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="font-medium">{onglet.libelle}</span>
                    <span
                      className={cn('text-[11px]', isActive ? 'text-slate-300' : 'text-slate-400')}
                    >
                      {onglet.resume}
                    </span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <Outlet />
    </div>
  )
}
